// resources/js/src/store/profileStore.ts
import { create } from "zustand";
import { profileApi } from "../services/profile.api";
import type { ProfileState, UserProfile } from "../services/profile.api";
import { ApiError } from "../services/api";

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function parseError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Network error";
}

function extractStateFromError(error: unknown): ProfileState | null {
  if (error instanceof ApiError) {
    const body = error.body as
      | { data?: { state?: ProfileState } }
      | undefined;

    if (body?.data?.state) return body.data.state;

    const legacyBody = error.body as { state?: ProfileState } | undefined;
    if (legacyBody?.state) return legacyBody.state;

    if (error.status === 404) return "not_found";
    if (error.status === 403) return "banned";
  }

  return null;
}

/*
|--------------------------------------------------------------------------
| Store Shape
|--------------------------------------------------------------------------
*/

type ProfileStoreState = {
  // Data
  profile: UserProfile | null;
  state: ProfileState | null;

  // UI State
  isLoading: boolean;
  error: string;

  // Actions
  load: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;

  // Optimistic UI
  setStateOptimistic: (newState: ProfileState) => void;
  rollbackState: (oldState: ProfileState) => void;

  // Helpers
  isOwner: () => boolean;
  isFriend: () => boolean;
  isBlocked: () => boolean;
  canShowPosts: () => boolean;
};

/*
|--------------------------------------------------------------------------
| Store
|--------------------------------------------------------------------------
*/

export const useProfileStore = create<ProfileStoreState>((set, get) => ({
  // ==================== Initial State ====================
  profile: null,
  state: null,
  isLoading: false,
  error: "",

  // ==================== Load ====================
  load: async (id) => {
    const { profile: existingProfile } = get();
    const isRefresh = !!existingProfile;

    // لو refresh: متعملش reset للبيانات عشان مفيش flicker
    set({
      isLoading: !isRefresh,
      error: "",
      ...(isRefresh ? {} : { profile: null, state: null }),
    });

    try {
      const res = await profileApi.show(id);

      set({
        profile: res.user,
        state: res.state,
        isLoading: false,
      });
    } catch (error) {
      const errState = extractStateFromError(error);

      if (errState) {
        set({
          profile: null,
          state: errState,
          isLoading: false,
          error: "",
        });
        return;
      }

      set({
        profile: null,
        state: null,
        isLoading: false,
        error: parseError(error),
      });
    }
  },

  // ==================== Refresh (بدون reset) ====================
  refresh: async () => {
    const { profile } = get();
    if (!profile) return;

    try {
      const res = await profileApi.show(profile.id);
      set({ profile: res.user, state: res.state });
    } catch {
      // نتجاهل الخطأ في refresh
    }
  },

  // ==================== Reset ====================
  reset: () =>
    set({
      profile: null,
      state: null,
      isLoading: false,
      error: "",
    }),

  // ==================== Optimistic UI ====================
  setStateOptimistic: (newState) => set({ state: newState }),

  rollbackState: (oldState) => set({ state: oldState }),

  // ==================== Helpers ====================
  isOwner: () => get().state === "owner",

  isFriend: () => get().state === "friend",

  isBlocked: () =>
    get().state === "blocked_by_me" || get().state === "blocked_by_them",

  canShowPosts: () => {
    const s = get().state;
    if (!s) return false;
    return ![
      "not_found",
      "banned",
      "blocked_by_me",
      "blocked_by_them",
    ].includes(s);
  },
}));